import { dataIdFromObject } from '@sketch/graphql-cache'

// Utils
import { useOnEvent } from '@sketch/utils'

// Commponents
import { handleFetchMore } from '@sketch/components'

// Hooks
import { useSortingSettings } from 'modules/shares/hooks/useSortSettings'
import { useFlag } from '@sketch/modules-common'

// Types
import {
  useGetProjectCollectionsQuery,
  useGetProjectSharesQuery,
} from '@sketch/gql-types'
import { useRestoreProjectBanner } from '../useRestoreProjectBanner/useRestoreProjectBanner'

interface UseProjectsProps {
  workspaceId: string
  projectId: string
  search?: string
}

// Helpers
const sharesEntriesPath = ['project', 'shares', 'entries']
const collectionsEntriesPath = ['project', 'collections', 'entries']

export const useProject = ({
  workspaceId,
  projectId,
  search,
}: UseProjectsProps) => {
  // TODO: Remove collections FF
  // https://github.com/sketch-hq/Cloud/issues/15999
  const isCollectionsFFEnabled = useFlag('collections')
  const isSearchFilterSortOn = useFlag('search-filter-sort')

  let filters
  if (!search && isCollectionsFFEnabled) {
    filters = ['NO_COLLECTION' as const]
  }

  const { sort } = useSortingSettings()
  const {
    data: projectSharesData,
    networkStatus: projectSharesNetworkStatus,
    fetchMore: fetchMoreProjectShares,
    error: projectSharesError,
    refetch: refetchProjectShares,
  } = useGetProjectSharesQuery({
    variables: {
      shortId: projectId,
      after: null,
      search: {
        name: search || null,
        isCurrentVersionDownloadable: null,
        filters,
      },
      sortOrder: isSearchFilterSortOn ? sort : undefined,
    },
    shouldInvalidatePrevious: (prev, curr) => prev?.shortId !== curr?.shortId,
  })

  const {
    data: collectionsData,
    networkStatus: collectionNetworkStatus,
    fetchMore: fetchMoreCollections,
    error: collectionsError,
    refetch: refetchCollections,
  } = useGetProjectCollectionsQuery({
    variables: {
      projectIdentifier: projectId,
      after: null,
    },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.projectIdentifier !== curr?.projectIdentifier,
    skip: !!search || !isCollectionsFFEnabled,
  })

  useOnEvent(
    'workspaceShareRefresh',
    ({ workspaceIdentifier, projectShortId }) => {
      if (workspaceIdentifier !== workspaceId || projectShortId !== projectId) {
        // Not the workspace visible ignore then
        return
      }
      refetchProjectShares()
      refetchCollections()
    }
  )

  useRestoreProjectBanner({ project: projectSharesData?.project, workspaceId })

  const loading = projectSharesNetworkStatus < 3 || collectionNetworkStatus < 3
  const error = projectSharesError || collectionsError

  const project = projectSharesData?.project
  const afterShares = project?.shares.meta.after ?? null
  const afterCollections =
    collectionsData?.project.collections.meta.after ?? null

  const loadMoreSharesHandler = handleFetchMore(
    fetchMoreProjectShares,
    sharesEntriesPath,
    { dataIdFromObject, after: afterShares }
  )

  const loadMoreCollectionsHandler = handleFetchMore(
    fetchMoreCollections,
    collectionsEntriesPath,
    { dataIdFromObject, after: afterCollections }
  )

  return {
    data: {
      project: projectSharesData?.project,
      collections: collectionsData?.project.collections,
    },
    loadMore: {
      project: loadMoreSharesHandler,
      collections: loadMoreCollectionsHandler,
    },
    loading,
    error,
  }
}
